/**
 * PRODUCT-SPECIFICATIONS MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * specPdf — a branded PDF of the product specifications grid, in one of two
 * layouts chosen from the size of the selection (backlog 217):
 *
 *   SHEET  (> ONE_UP_MAX SKUs) — LANDSCAPE. The sheet is wide (every fixed
 *     field + UPC photos + custom columns + Active), so columns are split into
 *     horizontal "panels": each panel repeats the SKU column as an anchor and
 *     shows as many further columns as fit the page width, and the rows
 *     paginate vertically within each panel.
 *   SPEC CARD (≤ ONE_UP_MAX SKUs) — PORTRAIT, one page per SKU. The old code
 *     always laid out the sheet, so exporting the single spec a buyer asked for
 *     produced four landscape pages whose first page was one data row above 85%
 *     white paper. A small selection now reads as a spec sheet, not a fragment
 *     of a spreadsheet.
 *
 * Headers are the company primary color; UPC photos are embedded.
 *
 * jsPDF is a project dependency, imported lazily so it stays out of the main
 * bundle. We render values + images only (no user HTML) — no injection surface.
 */

import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { supabase } from "../../../shared/utils/supabaseClient.js";

const BRAND_DEFAULT = [27, 42, 74];   // #1B2A4A navy
// The app orange (backlog 145/166; was #E85D26). Used as a 1.5pt rule on a white
// page, so it takes the LIGHT-theme TEXT value #8F5408 (4.57:1) rather than the
// #F2A33C fill, which is 2.08:1 on white. Only the default changes — a tenant's
// own secondaryColor still wins.
const ACCENT_DEFAULT = [143, 84, 8];  // #8F5408
const MUTED = [110, 120, 135];
const RULE = [210, 215, 224];
const ALT_ROW = [248, 250, 252];

// A selection this small gets the portrait one-page-per-SKU card instead of the
// wide sheet (backlog 217) — exporting one spec for one buyer is the single most
// likely export there is, and it should not come out as four landscape pages.
const ONE_UP_MAX = 3;

function hexToRgb(hex, fallback) {
  if (typeof hex === "string" && /^#[0-9a-fA-F]{6}$/.test(hex.trim())) {
    const h = hex.trim();
    return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  }
  return fallback;
}

function readableOn([r, g, b]) {
  return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? [20, 24, 32] : [255, 255, 255];
}

// Fetch + rasterize ANY image URL (svg/png/jpg/webp) to a PNG data URL. The
// data-URL path rasterizes every format and never taints the canvas.
async function loadImageDataUrl(url) {
  try {
    if (!url || typeof document === "undefined" || typeof fetch === "undefined") return null;
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const srcUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(new Error("read"));
      fr.readAsDataURL(blob);
    });
    return await rasterize(srcUrl);
  } catch { return null; }
}

function rasterize(srcUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let w = img.naturalWidth || img.width || 0;
      let h = img.naturalHeight || img.height || 0;
      if (!w || !h) { w = 300; h = 100; }
      const scale = Math.min(4, Math.max(2, 700 / w));
      try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL("image/png"), width: w, height: h });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = srcUrl;
  });
}

async function actorName() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "—";
  } catch { return "—"; }
}

// Per-column target widths (points). The first (SKU) column is the panel anchor.
const wOf = (c) =>
  c.key === "__name" ? 132 :
  c.kind === "num" ? 56 :
  c.kind === "active" ? 46 :
  c.kind === "img" ? 70 : 90;

/**
 * @param {Object} opts
 * @param {Array}  opts.columns  [{ label, key, kind:'dim'|'num'|'img'|'active' }]
 * @param {Array}  opts.rows     objects keyed by column.key (img keys hold a URL)
 * @param {string} [opts.title]
 * @returns {Promise<{ logo: 'ok'|'none'|'failed', layout: 'sheet'|'card' }>}
 *   `logo` lets the caller warn in the export dialog when the document went out
 *   without a mark (backlog 194).
 */
export async function exportProductSpecsPdf({ columns, rows, title = "Product Specifications" }) {
  const mod = await import("jspdf");
  const JsPDF = mod.jsPDF || mod.default || (mod.default && mod.default.jsPDF);
  if (typeof JsPDF !== "function") throw new Error("PDF engine failed to load.");

  let teamName = "", logoUrl = null, primary = BRAND_DEFAULT, accent = ACCENT_DEFAULT;
  let acctFailed = false;
  try {
    const acct = await getMyPrimaryAccount();
    teamName = acct?.name ?? "";
    logoUrl = acct?.logoUrl ?? null;
    primary = hexToRgb(acct?.primaryColor, BRAND_DEFAULT);
    accent = hexToRgb(acct?.secondaryColor, ACCENT_DEFAULT);
  } catch { acctFailed = true; /* unbranded */ }

  // Backlog 194: NEVER substitute the Brand Command mark into a customer's
  // document. This sheet is handed to their buyer — our logo on it is their
  // vendor's logo on their spec. No tenant logo (or a logo we cannot fetch)
  // means NO logo block at all; the header closes up and the caller is told so
  // it can warn before the user sends the file.
  const logo = logoUrl ? await loadImageDataUrl(logoUrl) : null;
  const logoStatus = logo ? "ok" : (logoUrl || acctFailed) ? "failed" : "none";

  const who = await actorName();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // Pre-fetch every referenced UPC photo once.
  const imgCols = columns.filter((c) => c.kind === "img");
  const urls = [...new Set(rows.flatMap((r) => imgCols.map((c) => r[c.key]).filter(Boolean)))];
  const imgMap = new Map();
  await Promise.all(urls.map(async (u) => { const d = await loadImageDataUrl(u); if (d) imgMap.set(u, d); }));

  // Layout choice (backlog 217): a handful of SKUs reads as a portrait spec
  // card, one page per SKU; a real list stays the landscape sheet.
  const oneUp = rows.length > 0 && rows.length <= ONE_UP_MAX;

  const doc = new JsPDF({ unit: "pt", format: "letter", orientation: oneUp ? "portrait" : "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 30;
  const contentW = pageW - margin * 2;
  const headerTextColor = readableOn(primary);

  const ROW_H = 30;
  const HEAD_H = 24;

  // Split columns into horizontal panels (anchor column repeated on each panel).
  // Sheet layout only — the card layout stacks fields instead of paginating them.
  const anchor = columns[0];
  const rest = columns.slice(1);
  const panels = [];
  if (!oneUp) {
    let cur = [], curW = wOf(anchor);
    for (const c of rest) {
      const cw = wOf(c);
      if (cur.length && curW + cw > contentW) { panels.push(cur); cur = []; curW = wOf(anchor); }
      cur.push(c); curW += cw;
    }
    if (cur.length) panels.push(cur);
    if (!panels.length) panels.push([]); // no extra columns → just the anchor
  }

  const fit = (text, w) => {
    let t = String(text ?? "");
    if (!t) return "";
    const maxW = w - 6;
    if (doc.getTextWidth(t) <= maxW) return t;
    while (t.length > 1 && doc.getTextWidth(t + "…") > maxW) t = t.slice(0, -1);
    return t + "…";
  };

  // `extra` is appended to the sub-line ("Columns 2 of 4", "SKU 1 of 3", …).
  // With no tenant logo there is simply no logo block: the title already sits at
  // the left margin, so the header closes up with no hole (backlog 194).
  const drawPageHeader = (extra = "") => {
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 6, "F");
    let y = margin;
    if (logo) {
      const maxH = 30, maxW = 130;
      let h = maxH, w = h * (logo.width / logo.height);
      if (w > maxW) { w = maxW; h = w * (logo.height / logo.width); }
      doc.addImage(logo.dataUrl, "PNG", pageW - margin - w, y - 2, w, h);
    }
    doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(...primary);
    doc.text(title, margin, y + 6);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    const sub = [teamName, `Exported ${dateStr}`, `By ${who}`, `${rows.length} SKU${rows.length === 1 ? "" : "s"}`]
      .filter(Boolean).join("    ·    ") + extra;
    doc.text(sub, margin, y + 20);
    doc.setDrawColor(...accent).setLineWidth(1.5);
    doc.line(margin, y + 26, pageW - margin, y + 26);
    return y + 32;
  };

  // Printed value for a non-image cell — shared by both layouts.
  const cellText = (c, row) => {
    if (c.kind === "active") return row[c.key] ? "Yes" : "No";
    const raw = row[c.key];
    return raw === "" || raw == null ? "—" : String(raw);
  };

  const drawTableHeader = (cols, xs, y) => {
    const right = xs[xs.length - 1] + wOf(cols[cols.length - 1]);
    doc.setFillColor(...primary);
    doc.rect(margin, y, right - margin, HEAD_H, "F");
    doc.setFont("helvetica", "bold").setFontSize(7.5).setTextColor(...headerTextColor);
    cols.forEach((c, i) => {
      const cw = wOf(c);
      const lines = doc.splitTextToSize(String(c.label).toUpperCase(), cw - 6).slice(0, 2);
      const startY = y + (HEAD_H - lines.length * 8) / 2 + 6;
      lines.forEach((ln, li) => doc.text(ln, xs[i] + cw / 2, startY + li * 8, { align: "center" }));
    });
    return y + HEAD_H;
  };

  const drawRow = (row, ri, cols, xs, y) => {
    const right = xs[xs.length - 1] + wOf(cols[cols.length - 1]);
    if (ri % 2 === 1) { doc.setFillColor(...ALT_ROW); doc.rect(margin, y, right - margin, ROW_H, "F"); }
    cols.forEach((c, i) => {
      const cw = wOf(c), cx = xs[i];
      if (c.kind === "img") {
        const im = row[c.key] ? imgMap.get(row[c.key]) : null;
        if (im) {
          const boxW = cw - 6, boxH = ROW_H - 6, ar = im.width / im.height;
          let w = boxW, h = w / ar;
          if (h > boxH) { h = boxH; w = h * ar; }
          doc.addImage(im.dataUrl, "PNG", cx + (cw - w) / 2, y + (ROW_H - h) / 2, w, h);
        } else {
          doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(...MUTED);
          doc.text("—", cx + cw / 2, y + ROW_H / 2 + 3, { align: "center" });
        }
        return;
      }
      if (c.kind === "active") {
        doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(40, 48, 64);
        doc.text(row[c.key] ? "Yes" : "No", cx + cw / 2, y + ROW_H / 2 + 3, { align: "center" });
        return;
      }
      const text = cellText(c, row);
      const isName = c.key === "__name";
      doc.setFont("helvetica", isName ? "bold" : "normal").setFontSize(7.5).setTextColor(40, 48, 64);
      if (c.kind === "num") {
        doc.text(fit(text, cw), cx + cw - 5, y + ROW_H / 2 + 3, { align: "right" });
      } else {
        doc.text(fit(text, cw), cx + 5, y + ROW_H / 2 + 3);
      }
    });
    doc.setDrawColor(...RULE).setLineWidth(0.3);
    doc.line(margin, y + ROW_H, right, y + ROW_H);
  };

  // One portrait page per SKU: the SKU name, then every non-image field as a
  // two-column label/value list, then the UPC photos it actually has. Nothing
  // is paginated across columns, so a one-SKU export is one page (backlog 217).
  const drawSpecCard = (row, idx) => {
    const extra = rows.length > 1 ? `    ·    SKU ${idx + 1} of ${rows.length}` : "";
    let y = drawPageHeader(extra);

    doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...primary);
    doc.text(fit(row.__name ?? "—", contentW), margin, y + 14);
    y += 30;

    // Two columns of label/value pairs. The field order already arrives grouped
    // (identity → regulatory → product → case → pallet → custom → Active).
    const fields = columns.filter((c) => c.key !== anchor?.key && c.kind !== "img");
    const colW = contentW / 2;
    const fieldW = colW - 16;
    const LINE_H = 20;
    let col = 0;
    fields.forEach((c) => {
      if (col === 0 && y + LINE_H > pageH - margin - 24) { doc.addPage(); y = drawPageHeader(extra); }
      const cx = margin + col * colW;
      doc.setFont("helvetica", "bold").setFontSize(7.5).setTextColor(...MUTED);
      doc.text(fit(String(c.label).toUpperCase(), fieldW * 0.62), cx, y + 12);
      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(40, 48, 64);
      doc.text(fit(cellText(c, row), fieldW * 0.42), cx + fieldW, y + 12, { align: "right" });
      doc.setDrawColor(...RULE).setLineWidth(0.3);
      doc.line(cx, y + 16, cx + fieldW, y + 16);
      col = (col + 1) % 2;
      if (col === 0) y += LINE_H;
    });
    if (col === 1) y += LINE_H;

    const photos = columns.filter((c) => c.kind === "img" && row[c.key] && imgMap.get(row[c.key]));
    if (photos.length) {
      const BOX_W = 150, BOX_H = 96;
      y += 10;
      if (y + BOX_H + 18 > pageH - margin - 24) { doc.addPage(); y = drawPageHeader(extra); }
      let ix = margin;
      photos.forEach((c) => {
        const im = imgMap.get(row[c.key]);
        const ar = im.width / im.height;
        let w = BOX_W, h = w / ar;
        if (h > BOX_H) { h = BOX_H; w = h * ar; }
        doc.addImage(im.dataUrl, "PNG", ix, y, w, h);
        doc.setFont("helvetica", "bold").setFontSize(7.5).setTextColor(...MUTED);
        doc.text(fit(String(c.label).toUpperCase(), BOX_W), ix, y + BOX_H + 10);
        ix += BOX_W + 20;
      });
    }
  };

  if (oneUp) {
    rows.forEach((row, idx) => {
      if (idx) doc.addPage();
      drawSpecCard(row, idx);
    });
  } else {
    let firstPage = true;
    panels.forEach((panel, pi) => {
      const cols = [anchor, ...panel];
      const xs = []; let x = margin;
      cols.forEach((c) => { xs.push(x); x += wOf(c); });
      const extra = panels.length > 1 ? `    ·    Columns ${pi + 1} of ${panels.length}` : "";

      if (!firstPage) doc.addPage();
      firstPage = false;
      let y = drawPageHeader(extra);
      y = drawTableHeader(cols, xs, y);

      rows.forEach((row, ri) => {
        if (y + ROW_H > pageH - margin - 16) {
          doc.addPage();
          y = drawPageHeader(extra);
          y = drawTableHeader(cols, xs, y);
        }
        drawRow(row, ri, cols, xs, y);
        y += ROW_H;
      });
      if (!rows.length) {
        doc.setFont("helvetica", "italic").setFontSize(9).setTextColor(...MUTED);
        doc.text("No products selected.", margin, y + 14);
      }
    });
  }

  // Page-number footer on every page.
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    doc.text(`${teamName ? teamName + " · " : ""}Product Specifications`, margin, pageH - 16);
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 16, { align: "right" });
  }

  const team = (teamName || "team").trim() || "team";
  const filename = `${team}_Product_Specs_${now.toISOString().slice(0, 10)}.pdf`.replace(/[^A-Za-z0-9._-]+/g, "_");
  doc.save(filename);

  return { logo: logoStatus, layout: oneUp ? "card" : "sheet" };
}
